import React, { useEffect, useRef } from 'react';

type OrbSize = 'big' | 'med' | 'small' | 'tiny';
type OrbTone = 'acc-1' | 'acc-2' | 'acc-3' | 'dark';
type OrbAnim = 'f-a' | 'f-b' | 'f-c';

const DEPTH_PX: Record<OrbSize, number> = {
  big: 8,
  med: 14,
  small: 22,
  tiny: 30,
};

const Orb: React.FC<{
  size: OrbSize;
  tone: OrbTone;
  anim: OrbAnim;
  style?: React.CSSProperties;
}> = ({ size, tone, anim, style }) => {
  const d = DEPTH_PX[size];
  return (
    <div
      className="orb-wrap"
      style={{
        position: 'absolute',
        transform: `translate3d(calc(var(--mx, 0) * ${d}px), calc(var(--my, 0) * ${d}px), 0)`,
        ...style,
      }}
    >
      <div className={`orb ${size} ${tone} ${anim}`} style={{ position: 'relative', top: 0, left: 0 }} />
    </div>
  );
};

const DotGrid: React.FC<{ cols?: number; rows?: number; style?: React.CSSProperties }> = ({
  cols = 6,
  rows = 5,
  style,
}) => {
  const cells = cols * rows;
  return (
    <div
      className="dot-grid"
      style={{
        ['--cols' as string]: cols,
        transform: 'translate3d(calc(var(--mx, 0) * 6px), calc(var(--my, 0) * 6px), 0)',
        ...style,
      } as React.CSSProperties}
    >
      {Array.from({ length: cells }).map((_, i) => (
        <span className="dot" key={i} />
      ))}
    </div>
  );
};

export const Background: React.FC = () => {
  const stageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    let frame = 0;
    let mx = 0;
    let my = 0;
    const target = { x: 0, y: 0 };

    const onMove = (e: MouseEvent) => {
      target.x = (e.clientX / window.innerWidth) * 2 - 1;
      target.y = (e.clientY / window.innerHeight) * 2 - 1;
      if (!frame) frame = requestAnimationFrame(tick);
    };

    const tick = () => {
      mx += (target.x - mx) * 0.08;
      my += (target.y - my) * 0.08;
      if (stageRef.current) {
        stageRef.current.style.setProperty('--mx', mx.toFixed(4));
        stageRef.current.style.setProperty('--my', my.toFixed(4));
      }
      if (Math.abs(target.x - mx) > 0.0005 || Math.abs(target.y - my) > 0.0005) {
        frame = requestAnimationFrame(tick);
      } else {
        frame = 0;
      }
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="bg-stage" aria-hidden="true" ref={stageRef}>
      <div className="bg-noise" />

      <Orb size="big"   tone="dark"  anim="f-a" style={{ top: '4%',  left: '64%' }} />
      <Orb size="small" tone="acc-1" anim="f-b" style={{ top: '12%', left: '12%' }} />
      <Orb size="med"   tone="acc-2" anim="f-c" style={{ top: '22%', left: '78%' }} />
      <Orb size="tiny"  tone="acc-1" anim="f-a" style={{ top: '8%',  left: '40%' }} />
      <DotGrid cols={6} rows={5} style={{ top: '18%', left: '2%' }} />
      <DotGrid cols={5} rows={4} style={{ top: '6%',  right: '4%' }} />

      <Orb size="med"   tone="dark"  anim="f-b" style={{ top: '40%', left: '46%' }} />
      <Orb size="small" tone="acc-1" anim="f-a" style={{ top: '52%', left: '88%' }} />
      <Orb size="tiny"  tone="acc-2" anim="f-c" style={{ top: '58%', left: '8%'  }} />
      <Orb size="big"   tone="acc-3" anim="f-b" style={{ top: '70%', left: '92%' }} />
      <DotGrid cols={5} rows={4} style={{ top: '46%', right: '8%' }} />
      <DotGrid cols={6} rows={4} style={{ top: '64%', left: '6%' }} />

      <Orb size="med"   tone="acc-1" anim="f-a" style={{ top: '82%', left: '20%' }} />
      <Orb size="small" tone="acc-2" anim="f-c" style={{ top: '86%', left: '52%' }} />
      <Orb size="big"   tone="dark"  anim="f-b" style={{ top: '92%', left: '78%' }} />
      <Orb size="tiny"  tone="acc-1" anim="f-a" style={{ top: '78%', left: '70%' }} />

      <Orb size="med"   tone="acc-3" anim="f-c" style={{ top: '110%', left: '10%' }} />
      <Orb size="small" tone="acc-1" anim="f-a" style={{ top: '120%', left: '58%' }} />
      <Orb size="tiny"  tone="acc-2" anim="f-b" style={{ top: '130%', left: '82%' }} />
      <Orb size="big"   tone="dark"  anim="f-a" style={{ top: '140%', left: '14%' }} />
      <DotGrid cols={5} rows={4} style={{ top: '115%', right: '6%' }} />
      <DotGrid cols={6} rows={5} style={{ top: '145%', left: '4%' }} />

      <Orb size="med"   tone="acc-1" anim="f-c" style={{ top: '160%', left: '70%' }} />
      <Orb size="small" tone="acc-3" anim="f-b" style={{ top: '175%', left: '20%' }} />
      <Orb size="tiny"  tone="acc-2" anim="f-a" style={{ top: '185%', left: '50%' }} />
    </div>
  );
};
